import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add, Close } from '@mui/icons-material'
import { Box, Button, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { GameIcon } from '../../../../../features/game'
import { Game } from '../../../../../features/game/types/Game'
import { borderRadiusDefault } from '../../../../../themes/theme'
import { useGetInternalGamesByAppIdsQuery } from '../../../../api/internal-games'
import { InternalGameSelectorDialog } from '../../../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'
import { mergeExampleAppIds } from '../../helpers/conventionalGenreHelpers'

type ExampleGamesFieldProps = {
  value: number[]
  onChange: (appIds: number[]) => void
  max: number
}

export const ExampleGamesField: FC<ExampleGamesFieldProps> = ({ value, onChange, max }) => {
  const { t } = useTranslation()
  const [knownGames, setKnownGames] = useState<Map<number, Game>>(new Map())
  const [requestedAppIds, setRequestedAppIds] = useState<Set<number>>(new Set())
  const [selectorOpen, setSelectorOpen] = useState(false)

  const missingAppIds = useMemo(() => value.filter((appId) => !knownGames.has(appId) && !requestedAppIds.has(appId)), [knownGames, requestedAppIds, value])
  const gamesQuery = useGetInternalGamesByAppIdsQuery(missingAppIds, { skip: !missingAppIds.length })

  useEffect(() => {
    if (gamesQuery.isFetching || !(gamesQuery.isSuccess || gamesQuery.isError)) return
    const args = gamesQuery.originalArgs ?? []
    if (gamesQuery.isSuccess) setKnownGames((current) => new Map([...current, ...gamesQuery.data.map((game): [number, Game] => [game.appId, game])]))
    setRequestedAppIds((current) => new Set([...current, ...args]))
  }, [gamesQuery.data, gamesQuery.isError, gamesQuery.isFetching, gamesQuery.isSuccess, gamesQuery.originalArgs])

  const resolvedAppIds = useMemo(() => new Set(value.filter((appId) => knownGames.has(appId))), [knownGames, value])
  const selectedGames = useMemo(() => value.flatMap((appId) => knownGames.get(appId) ?? []), [knownGames, value])
  const unresolvedCount = value.length - resolvedAppIds.size

  const handleDone = (games: Game[]) => {
    setKnownGames((current) => new Map([...current, ...games.map((game): [number, Game] => [game.appId, game])]))
    onChange(
      mergeExampleAppIds(
        value,
        resolvedAppIds,
        games.map((game) => game.appId),
        max
      )
    )
    setSelectorOpen(false)
  }

  return (
    <Stack spacing={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight="bold">
          {t('internal-service-management:conventional_genres_example_games')} ({value.length}/{max})
        </Typography>
        {/* The selector starts from the found games, so opening it before they load would drop them on Done. */}
        <Button size="small" variant="outlined" startIcon={<Add />} disabled={!!missingAppIds.length} onClick={() => setSelectorOpen(true)}>
          {t('internal-service-management:conventional_genres_add_games')}
        </Button>
      </Box>
      {!value.length ? (
        <Typography variant="body2" color="text.secondary">
          {t('internal-service-management:conventional_genres_no_example_games')}
        </Typography>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={2}>
          {value.map((appId) => {
            const game = knownGames.get(appId)
            const isLoading = !game && !requestedAppIds.has(appId)
            return (
              <Stack key={appId} alignItems="center" spacing={0.5} sx={{ width: 84 }}>
                {isLoading ? (
                  <Skeleton variant="rounded" width={56} height={56} />
                ) : game ? (
                  <GameIcon src={game.getIcon() as string} gameName={game.name ?? ''} size="small" />
                ) : (
                  <Tooltip title={t('internal-service-management:conventional_genres_unknown_game_help')}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: borderRadiusDefault,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ?
                    </Box>
                  </Tooltip>
                )}
                <Typography variant="caption" textAlign="center" noWrap sx={{ width: '100%' }}>
                  {game?.name ?? (isLoading ? '' : t('internal-service-management:conventional_genres_unknown_game', { appId }))}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t('internal-service-management:conventional_genres_remove_game', { name: game?.name ?? appId })}
                  onClick={() => onChange(value.filter((id) => id !== appId))}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Stack>
            )
          })}
        </Box>
      )}
      <InternalGameSelectorDialog
        open={selectorOpen}
        title={t('internal-service-management:conventional_genres_add_games')}
        selectedGames={selectedGames}
        maxSelected={Math.max(max - unresolvedCount, 1)}
        onClose={() => setSelectorOpen(false)}
        onDone={handleDone}
      />
    </Stack>
  )
}
